/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType } from '@angular/cdk/overlay';
import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	AdjustmentTypeLabels,
	CondoUnit,
	MatterAdjustment,
	MatterAdjustmentCalculated,
	MatterAdjustmentCommonExpenses,
	MatterAdjustmentType,
	MatterAdjustmentVtb,
	MatterFileType,
	UnitType,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessDocumentTemplatesService } from '@repos/document-templates/business-document-templates.service';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { combineLatest, Observable, Subject, timer } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { CommonExpensesDialogComponent } from './common-expenses-dialog';
import { DepositDialogComponent } from './deposit-dialog';
import { HeatDialogComponent } from './heat-dialog';
import { OccupancyFeesDialogComponent } from './occupancy-fees-dialog/occupancy-fees-dialog.component';
import { PropertyTaxDialogComponent } from './property-tax-dialog';
import { SalePriceDialogComponent } from './sale-price-dialog';
import { TarrionWarrantyDialogComponent } from './tarrion-warranty-dialog';
import { TenancyCurrentDialogComponent } from './tenancy-current-dialog';
import { TenancyPrepaidDialogComponent } from './tenancy-prepaid-dialog';
import { DropdownResult } from '../../../shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import {
	AdjustmentsSelector,
	AllAdjustmentsCalculated,
} from '@state/matter/adjustments/adjustments.selectors';
import { OtherDialogComponent } from './other-dialog/other-dialog.component';
import { fade, openClose } from '@core/animations/animations';
import { capitalize } from '@core/util/string';
import { BusinessState } from '@state/business';
import { BusinessSettings } from '@models/businessSettings/settings';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { cloneDeep } from 'lodash';
import { getMatterAdjustmentOrder } from '@core/util/adjustments';
import { HstSalePriceDialogComponent } from './hst-sale-price-dialog/hst-sale-price-dialog.component';
import { HstRebateDialogComponent } from './hst-rebate-dialog/hst-rebate-dialog.component';
import { HCRADialogComponent } from './hcra-dialog/hcra-dialog.component';
import { VacantPropertyDialogComponent } from './vacant-property-dialog/vacant-property-dialog.component';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { Jurisdiction } from '@models/jurisdictions';
import { ICON } from '@core/util/icon';
import { isSet } from '@core/util/value';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { MatterMortgage } from '@models/mortgage';
import { VtbMortgageDialogComponent } from './vtb-mortgage-dialog/vtb-mortgage-dialog.component';
import { MatterWordDocumentsDefaultsSource } from '@state/matter/matter-word-documents/matter-word-documents-defaults-source.selectors';
import { AddPropertyTaxConfirmationModalComponent } from './add-property-tax-confirmation-modal/add-property-tax-confirmation-modal.component';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { UI_TYPES } from 'src/app/ui/modal/modal.types';
import { TestHooks } from './adjustments.hooks';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { MatterTitlesDispatch } from '@state/matter/titles/titles.dispatch';
import { Logger } from '@core/logger';
import { SnackbarService } from '@services/ui/snackbar.service';
import { AdjustmentData } from './adjustments.types';

type Adjustment<T extends MatterAdjustment> = {
	component: ComponentType<any>;
	name: string;
	default: boolean;
	deletable: boolean;
	reorderable: boolean;
	requiresDate: boolean;
	nameOverride?: string;
	calculated?: MatterAdjustmentCalculated<T>;
};

@UntilDestroy()
@Component({
	selector: 'app-adjustments',
	templateUrl: './adjustments.component.html',
	styleUrls: ['./adjustments.component.scss'],
	animations: [fade(), openClose()],

	standalone: false,
})
export class AdjustmentsComponent implements OnInit {
	testHooks = TestHooks;
	private store = inject(Store);

	@Select(SelectedMatter.useHtmlDocuments)
	public useHtmlDocuments$: Observable<boolean>;
	@Select(SelectedMatter.adjustmentDate) adjustmentDate$: Observable<string>;
	@Select(SelectedMatter.overriddenBalanceDueOnClosing)
	balanceDueOnClosing$: Observable<number>;
	@Select(AdjustmentsSelector.allAdjustmentsCalculated)
	allCalculations$: Observable<AllAdjustmentsCalculated>;
	@Select(AdjustmentsSelector.vtbMortgageAdjustment)
	vtbMortgageAdjustment$: Observable<MatterAdjustmentVtb>;
	@ViewSelectSnapshot(SelectedMatter.closingDate) closingDate: string;
	@ViewSelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@ViewSelectSnapshot(SelectedMatter.overrideAdjustments)
	overrideAdjustments: boolean;
	@Select(BusinessState.getShowWordSideBySide)
	showWordSideBySide$: Observable<boolean>;
	@Select(
		MatterWordDocumentsDefaultsSource.getStatementOfAdjustmentsSourceIdByMatterType,
	)
	statementOfAdjustmentsDocSourceIdByMatterType$: Observable<string>;

	public matterTitles = this.store.selectSignal(MatterTitlesState.getAll);

	public matterFileTypes = MatterFileType;

	private highlightedEventStream = new Subject<string>();

	highlightedAdjustment: string = null;
	adjustmentDate: string;
	adjustmentDateFlag: boolean;
	adjustmentItems: Adjustment<any>[];
	availableAdjustments: Adjustment<any>[] = [];
	adjustmentItemsVisible: boolean;

	public ICON = ICON;

	readonly allAdjustmentItems: {
		[id in MatterAdjustmentType]: Adjustment<any>;
	} = {
		[MatterAdjustmentType.CommonExpenses]: {
			component: CommonExpensesDialogComponent,
			name: AdjustmentTypeLabels.CommonExpenses,
			default: false,
			deletable: true,
			reorderable: true,
			requiresDate: true,
		},
		[MatterAdjustmentType.Deposit]: {
			component: DepositDialogComponent,
			name: AdjustmentTypeLabels.Deposit,
			default: true,
			deletable: true,
			reorderable: true,
			requiresDate: false,
		},
		[MatterAdjustmentType.Heat]: {
			component: HeatDialogComponent,
			name: AdjustmentTypeLabels.Heat,
			default: false,
			deletable: true,
			reorderable: true,
			requiresDate: false,
		},
		[MatterAdjustmentType.OccupancyFees]: {
			component: OccupancyFeesDialogComponent,
			name: AdjustmentTypeLabels.OccupancyFees,
			default: false,
			deletable: true,
			reorderable: true,
			requiresDate: true,
		},
		[MatterAdjustmentType.PropertyTax]: {
			component: PropertyTaxDialogComponent,
			name: AdjustmentTypeLabels.PropertyTaxes,
			default: true,
			deletable: true,
			reorderable: true,
			requiresDate: true,
		},
		[MatterAdjustmentType.SalePrice]: {
			component: SalePriceDialogComponent,
			name: AdjustmentTypeLabels.SalePrice,
			default: true,
			deletable: false,
			reorderable: false,
			requiresDate: false,
		},
		[MatterAdjustmentType.VtbMortgage]: {
			component: VtbMortgageDialogComponent,
			name: AdjustmentTypeLabels.VtbMortgage,
			default: false,
			deletable: false,
			reorderable: true,
			requiresDate: false,
		},
		[MatterAdjustmentType.SalePriceHST]: {
			component: HstSalePriceDialogComponent,
			name: AdjustmentTypeLabels.HstOnSalePrice,
			default: false,
			deletable: false,
			reorderable: false,
			requiresDate: false,
		},
		[MatterAdjustmentType.SalePriceRebates]: {
			component: HstRebateDialogComponent,
			name: AdjustmentTypeLabels.HstRebate,
			default: false,
			deletable: false,
			reorderable: false,
			requiresDate: false,
		},
		[MatterAdjustmentType.TarionWarranty]: {
			component: TarrionWarrantyDialogComponent,
			name: AdjustmentTypeLabels.TarionWarranty,
			default: false,
			requiresDate: false,
			deletable: true,
			reorderable: true,
		},
		[MatterAdjustmentType.HCRA]: {
			component: HCRADialogComponent,
			name: AdjustmentTypeLabels.HcraFee,
			default: false,
			requiresDate: false,
			deletable: true,
			reorderable: true,
		},
		[MatterAdjustmentType.TenancyCurrent]: {
			component: TenancyCurrentDialogComponent,
			name: AdjustmentTypeLabels.TenancyCurrent,
			default: false,
			deletable: true,
			reorderable: true,
			requiresDate: true,
		},
		[MatterAdjustmentType.TenancyPrepaid]: {
			component: TenancyPrepaidDialogComponent,
			name: AdjustmentTypeLabels.TenancyPrepaid,
			default: false,
			deletable: true,
			reorderable: true,
			requiresDate: true,
		},
		[MatterAdjustmentType.Other]: {
			component: OtherDialogComponent,
			name: AdjustmentTypeLabels.Other,
			default: false,
			deletable: true,
			reorderable: true,
			requiresDate: true,
		},
		[MatterAdjustmentType.VacantTax]: {
			component: VacantPropertyDialogComponent,
			name: AdjustmentTypeLabels.VacantHomeTax,
			default: false,
			deletable: true,
			reorderable: true,
			requiresDate: false,
		},
	};

	documentId: string;
	public adjustmentTypes = MatterAdjustmentType;
	public balanceDueOnClosing: number;

	@ViewSelectSnapshot(SelectedMatter.matterHST) hst: number;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@ViewSelectSnapshot(BusinessState.getDefaultSettings)
	settings: BusinessSettings;
	@ViewSelectSnapshot(AdjustmentsSelector.totalBalanceCalculated)
	salePrice: number;
	@ViewSelectSnapshot(AdjustmentsSelector.showTarionWarrantyAdjustmentWarning)
	showTarionWarrantyAdjustmentWarning: boolean;

	@Select(MatterJurisdictionSelectors.getVacantTaxApplicableJurisdiction)
	vacantTaxApplicableJurisdiction$: Observable<Jurisdiction>;

	@Select(MatterMortgagesState.getVTBMortgage)
	vtbMortgage$: Observable<MatterMortgage>;

	constructor(
		private dialog: MatDialog,
		private businessDocumentTemplatesService: BusinessDocumentTemplatesService,
		private matterDispatch: MatterDispatch,
		private adjustmentDispatch: AdjustmentsDispatch,
		private modalService: ModalService,
		private matterTitlesDispatch: MatterTitlesDispatch,
		private snackbar: SnackbarService,
	) {
		this.highlightedEventStream
			.pipe(
				untilDestroyed(this),
				debounce(() => timer(200)),
			)
			.subscribe((x) => {
				this.highlightedAdjustment = x;
			});

		this.adjustmentDate$
			.pipe(untilDestroyed(this))
			.subscribe((adjustmentDate) => {
				this.adjustmentDate = adjustmentDate || null;
				this.adjustmentDateFlag = this.adjustmentDate === this.closingDate;
				if (!this.adjustmentDate) {
					this.adjustmentDateFlag = true;
					this.adjustmentDate = this.closingDate;
				}
			});

		this.adjustmentItemsVisible = false;
	}

	ngOnInit(): void {
		combineLatest([
			this.allCalculations$,
			this.vacantTaxApplicableJurisdiction$,
			this.balanceDueOnClosing$,
			this.vtbMortgageAdjustment$,
		])
			.pipe(untilDestroyed(this))

			.subscribe(
				([
					calculations,
					vacantTaxJurisdiction,
					balanceDueOnClosing,
					vtbMortgageAdjustment,
				]) => {
					this.balanceDueOnClosing = balanceDueOnClosing;
					this.businessDocumentTemplatesService.triggerUpdate(
						this.documentId,
						this.matterId,
					);
					const items: Adjustment<any>[] = [];
					const availableItems: Adjustment<any>[] = [];

					const salePrice =
						this.allAdjustmentItems[MatterAdjustmentType.SalePrice];
					items.push({
						...salePrice,
						calculated: calculations?.salePriceCalculated,
					});

					if (calculations?.salePriceHSTCalculated) {
						const salePriceHST =
							this.allAdjustmentItems[MatterAdjustmentType.SalePriceHST];
						items.push({
							...salePriceHST,
							calculated: calculations.salePriceHSTCalculated,
						});
					}

					if (calculations?.salePriceRebatesCalculated) {
						const salePriceRebates =
							this.allAdjustmentItems[MatterAdjustmentType.SalePriceRebates];
						items.push({
							...salePriceRebates,
							calculated: calculations.salePriceRebatesCalculated,
						});
					}

					if (calculations?.depositCalculated) {
						const deposit =
							this.allAdjustmentItems[MatterAdjustmentType.Deposit];
						items.push({
							...deposit,
							calculated: calculations.depositCalculated,
						});
					} else {
						availableItems.push(
							this.allAdjustmentItems[MatterAdjustmentType.Deposit],
						);
					}

					if (
						calculations?.propertyTaxCalculated &&
						calculations?.propertyTaxCalculated.length > 0
					) {
						calculations?.propertyTaxCalculated?.forEach((x) => {
							const propertyTax =
								this.allAdjustmentItems[MatterAdjustmentType.PropertyTax];

							items.push({
								...propertyTax,
								calculated: x,
								nameOverride: x.adjustment.title || 'Property taxes',
							});
						});
					} else {
						availableItems.push(
							this.allAdjustmentItems[MatterAdjustmentType.PropertyTax],
						);
					}

					if (calculations?.commonExpensesCalculated?.length > 0) {
						calculations.commonExpensesCalculated.forEach((x) => {
							const commonExpenses =
								this.allAdjustmentItems[MatterAdjustmentType.CommonExpenses];

							items.push({
								...commonExpenses,
								calculated: x,
								nameOverride: x.adjustment.title || 'Common expenses',
							});
						});
					} else {
						availableItems.push(
							this.allAdjustmentItems[MatterAdjustmentType.CommonExpenses],
						);
					}

					if (calculations?.occupancyFeesCalculated) {
						const occupancyFees =
							this.allAdjustmentItems[MatterAdjustmentType.OccupancyFees];
						items.push({
							...occupancyFees,
							calculated: calculations.occupancyFeesCalculated,
						});
					} else {
						availableItems.push(
							this.allAdjustmentItems[MatterAdjustmentType.OccupancyFees],
						);
					}

					calculations.heatCalculations?.forEach((x) => {
						const commonExpenses =
							this.allAdjustmentItems[MatterAdjustmentType.Heat];
						items.push({
							...commonExpenses,
							calculated: x,
							nameOverride: `Heat: ${capitalize(x?.adjustment?.heatType)}`,
						});
					});

					if (calculations?.tarionWarrantyCalculated) {
						const tarion =
							this.allAdjustmentItems[MatterAdjustmentType.TarionWarranty];
						items.push({
							...tarion,
							calculated: calculations.tarionWarrantyCalculated,
						});
					} else {
						availableItems.push(
							this.allAdjustmentItems[MatterAdjustmentType.TarionWarranty],
						);
					}

					if (calculations?.HCRACalculated) {
						const hcra = this.allAdjustmentItems[MatterAdjustmentType.HCRA];
						items.push({
							...hcra,
							calculated: calculations.HCRACalculated,
						});
					} else {
						availableItems.push(
							this.allAdjustmentItems[MatterAdjustmentType.HCRA],
						);
					}
					if (calculations?.vacantTaxCalculated) {
						const vacantTax =
							this.allAdjustmentItems[MatterAdjustmentType.VacantTax];
						items.push({
							...vacantTax,
							calculated: calculations.vacantTaxCalculated,
							nameOverride: calculations.vacantTaxCalculated.adjustment.title,
						});
					} else if (vacantTaxJurisdiction) {
						availableItems.push(
							this.allAdjustmentItems[MatterAdjustmentType.VacantTax],
						);
					}

					calculations?.tenancyCurrentCalculations?.forEach((x) => {
						const tenancyCurrent =
							this.allAdjustmentItems[MatterAdjustmentType.TenancyCurrent];
						items.push({
							...tenancyCurrent,
							nameOverride:
								x.adjustment?.title?.length > 0
									? x.adjustment?.title
									: 'Tenancy',
							calculated: x,
						});
					});

					calculations?.tenancyPrepaidCalculations?.forEach((x) => {
						const tenancyPrepaid =
							this.allAdjustmentItems[MatterAdjustmentType.TenancyPrepaid];
						items.push({
							...tenancyPrepaid,
							nameOverride:
								x.adjustment?.title?.length > 0
									? x.adjustment?.title
									: 'Pre-paid rent',
							calculated: x,
						});
					});

					calculations?.otherCalculations?.forEach((x) => {
						const other = this.allAdjustmentItems[MatterAdjustmentType.Other];
						items.push({
							...other,
							nameOverride: x.adjustment.title,
							calculated: x,
						});
					});

					if (calculations?.vtbMortgageCalculated && vtbMortgageAdjustment) {
						const vtbMortgage =
							this.allAdjustmentItems[MatterAdjustmentType.VtbMortgage];
						items.push({
							...vtbMortgage,
							nameOverride: vtbMortgageAdjustment.title,
							calculated: calculations.vtbMortgageCalculated,
						});
					}

					this.adjustmentItems = items?.sort((a, b) => {
						const orderA = getMatterAdjustmentOrder(a.calculated?.adjustment);
						const orderB = getMatterAdjustmentOrder(b.calculated?.adjustment);
						return orderA - orderB;
					});

					availableItems.push(
						this.allAdjustmentItems[MatterAdjustmentType.Heat],
					);
					availableItems.push(
						this.allAdjustmentItems[MatterAdjustmentType.TenancyCurrent],
					);
					availableItems.push(
						this.allAdjustmentItems[MatterAdjustmentType.TenancyPrepaid],
					);
					availableItems.push(
						this.allAdjustmentItems[MatterAdjustmentType.Other],
					);

					if (
						calculations?.propertyTaxCalculated &&
						calculations?.propertyTaxCalculated?.length > 0
					) {
						availableItems.push(
							this.allAdjustmentItems[MatterAdjustmentType.PropertyTax],
						);
					}

					this.availableAdjustments = availableItems;
				},
			);
	}

	drop(event: CdkDragDrop<string[]>): void {
		const reOrderedAdjustments = cloneDeep(this.adjustmentItems);
		const nextAllowedIndex = this.nextAllowedIndex();
		const newIndex =
			event.currentIndex < nextAllowedIndex
				? nextAllowedIndex
				: event.currentIndex;
		moveItemInArray(reOrderedAdjustments, event.previousIndex, newIndex);
		this.adjustmentItems = reOrderedAdjustments;
		const adjustments = reOrderedAdjustments
			?.map((x) => x.calculated?.adjustment as MatterAdjustment)
			?.filter(
				(x) =>
					x.type !== MatterAdjustmentType.SalePriceHST &&
					x.type !== MatterAdjustmentType.SalePriceRebates,
			);
		this.adjustmentDispatch.SetOrder(adjustments);
	}

	private nextAllowedIndex = () => {
		const salePriceHST = this.adjustmentItems?.findIndex(
			(x) =>
				x?.calculated?.adjustment?.type === MatterAdjustmentType.SalePriceHST,
		);
		const salePriceRebates = this.adjustmentItems?.findIndex(
			(x) =>
				x?.calculated?.adjustment?.type ===
				MatterAdjustmentType.SalePriceRebates,
		);
		const salePrice = this.adjustmentItems?.findIndex(
			(x) => x?.calculated?.adjustment?.type === MatterAdjustmentType.SalePrice,
		);

		if (salePriceRebates !== -1) {
			return salePriceRebates + 1;
		}

		if (salePriceHST !== -1) {
			return salePriceHST + 1;
		}

		return salePrice + 1;
	};

	setOnHover(id: string) {
		if (id !== null && id !== this.highlightedAdjustment) {
			this.highlightedAdjustment = id;
		} else {
			this.highlightedEventStream.next(id);
		}
	}

	addAdjustment(event: DropdownResult<Adjustment<any>>): void {
		const AdjustmentItem = event.result;
		this.closeAdjustmentList();

		if (
			AdjustmentItem ===
			this.allAdjustmentItems[MatterAdjustmentType.PropertyTax]
		) {
			const existingPropertyTaxAdjustments = this.adjustmentItems?.filter(
				(item) =>
					item.calculated?.adjustment?.type ===
					MatterAdjustmentType.PropertyTax,
			);

			if (
				existingPropertyTaxAdjustments?.length === 1 &&
				localStorage.getItem('skipPropertyTaxConfirmation') !== 'true'
			) {
				this.modalService.openModal(AddPropertyTaxConfirmationModalComponent, {
					title: 'Add Another Property Tax Adjustment?',
					data: {
						title: 'Confirm Additional Property Tax',
						message:
							'You already have a property tax adjustment. Are you sure you want to add another one?',
					},
					size: 'sm',
					type: UI_TYPES.WARNING,
					primaryAction: {
						text: 'Yes, Add Another',
						action: async () => {
							try {
								this.modalService.setPrimaryActionLoading(true);
								this.openDialog<typeof AdjustmentItem.component>(
									AdjustmentItem.component,
								);
								this.modalService.closeModal();
							} finally {
								this.modalService.setPrimaryActionLoading(false);
							}
						},
					},
					secondaryAction: {
						text: 'Cancel',
						action: () => {
							this.modalService.closeModal();
						},
					},
				});
				return;
			}
		}

		this.openDialog<typeof AdjustmentItem.component>(AdjustmentItem.component);
	}

	trackByadjustmentId(_, adjustment: Adjustment<any>): string {
		return adjustment?.calculated?.adjustment?.id as string;
	}

	changed(): void {
		this.matterDispatch.SetAdjustmentDate(this.matterId, this.adjustmentDate);
		this.businessDocumentTemplatesService.triggerUpdate(
			this.documentId,
			this.matterId,
		);
	}

	closeAdjustmentList(): void {
		this.adjustmentItemsVisible = false;
	}

	openDialog<T>(dialogComponent: ComponentType<T>, adjustmentId = ''): void {
		this.adjustmentItemsVisible = false;
		this.dialog.open(dialogComponent, {
			autoFocus: true,
			data: {
				adjustmentId,
			} as AdjustmentData,
			maxHeight: '100%',
			maxWidth: '900px',
			width: '90%',
		});
	}

	unlinkTitleCondoUnit(adjustment: MatterAdjustment) {
		const linkedTitleCondoUnitId = (
			adjustment as MatterAdjustmentCommonExpenses
		).linkedTitleCondoUnitId;

		if (
			adjustment.type !== MatterAdjustmentType.CommonExpenses ||
			!linkedTitleCondoUnitId
		) {
			return;
		}

		const linkedTitleCondoUnit = this.matterTitles().find(
			(x) => x.id === linkedTitleCondoUnitId,
		) as CondoUnit | undefined;

		if (!linkedTitleCondoUnit) {
			Logger.error(
				`Error encountered during deletion of SoAdj common expenses item id ${adjustment.id} which is linked to title condo unit id ${linkedTitleCondoUnitId} but no condo unit was found matching that id.`,
			);
			return;
		}

		const titleCondoUnitPayload: CondoUnit = {
			...linkedTitleCondoUnit,
			commonExpenses: 0,
			linkedSoAdjCommonExpensesId: null,
		};

		if (linkedTitleCondoUnit.unitType !== UnitType.COMMERCIAL_UNIT) {
			titleCondoUnitPayload.applyHst = false;
		}

		this.matterTitlesDispatch.CreateOrUpdate(titleCondoUnitPayload);

		this.snackbar.openSnackBar({
			text: 'Common expenses updated and will reflect on property details.',
			icon: ICON.CIRCLE_CHECK,
			type: 'success',
		});
	}

	removeAdjustment(adjustment: MatterAdjustment): void {
		// Common expenses adjustment items can be linked to a title condo unit for 2-way updates. When a common expenses adjustment item is deleted, any linked title condo unit must be unlinked.
		if (
			adjustment.type === MatterAdjustmentType.CommonExpenses &&
			(adjustment as MatterAdjustmentCommonExpenses).linkedTitleCondoUnitId
		) {
			this.unlinkTitleCondoUnit(adjustment);
		}

		this.adjustmentDispatch.Remove(adjustment);
		this.businessDocumentTemplatesService.triggerUpdate(
			this.documentId,
			this.matterId,
		);
	}

	setAdjustmentDateToClosingDate(): void {
		this.adjustmentDate = this.closingDate;
		this.changed();
	}

	toggleAdjustment(): void {
		if (this.overrideAdjustments) return;

		this.adjustmentItemsVisible = !this.adjustmentItemsVisible;
	}

	overrideAdjustmentsChanged($event: boolean): void {
		this.matterDispatch.SetAdjustmentsOverride(this.matterId, $event);
		if (!isSet(this.balanceDueOnClosing)) {
			this.balanceDueOnClosingChanged(this.salePrice);
		}
	}

	balanceDueOnClosingChanged($event: number): void {
		this.matterDispatch.SetOverriddenBalanceDueOnClosing(this.matterId, $event);
	}
}
